import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, Home, Calendar, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");

  const [status, setStatus] = useState("loading"); // loading, success, failed
  const [booking, setBooking] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else if (bookingId) {
      fetchBooking();
    } else {
      setStatus("failed");
    }
  }, [sessionId, bookingId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus("failed");
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (res.data.payment_status === "paid") {
        setStatus("success");
        if (bookingId) {
          fetchBooking();
        }
        return;
      } else if (res.data.status === "expired") {
        setStatus("failed");
        return;
      }

      // Continue polling
      setPollCount((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (e) {
      console.error("Error checking payment status:", e);
      setPollCount((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const fetchBooking = async () => {
    if (!bookingId) return;
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(res.data);
      if (res.data.payment_status === "paid") {
        setStatus("success");
      }
    } catch (e) {
      console.error("Error fetching booking:", e);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
      <div className="max-w-lg w-full">
        {status === "loading" && (
          <div data-testid="loading-state" className="text-center">
            <div className="w-20 h-20 bg-[#e6f2f1] rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[#134E4A] animate-spin" />
            </div>
            <h1 className="font-serif text-3xl text-[#1C1917] mb-4">
              Processing Payment...
            </h1>
            <p className="text-[#78716C]">
              Please wait while we confirm your payment
            </p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="success-state" className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-serif text-3xl text-[#1C1917] mb-4">
              Booking Confirmed!
            </h1>
            <p className="text-[#78716C] mb-8">
              Thank you for choosing Chestnut Hill Inn. We can't wait to host you!
            </p>

            {booking && (
              <div className="bg-white border border-stone-200 p-6 mb-8 text-left">
                <h2 className="font-serif text-xl text-[#1C1917] mb-6 text-center">
                  Booking Details
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Calendar className="w-5 h-5 text-[#134E4A] mt-0.5" />
                    <div>
                      <p className="text-sm text-[#78716C]">Dates</p>
                      <p className="text-[#1C1917]">
                        {booking.check_in} to {booking.check_out}
                      </p>
                      <p className="text-sm text-[#78716C]">
                        {booking.total_nights} nights
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Home className="w-5 h-5 text-[#134E4A] mt-0.5" />
                    <div>
                      <p className="text-sm text-[#78716C]">Guests</p>
                      <p className="text-[#1C1917]">{booking.guest_count} guests</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 text-[#134E4A] mt-0.5" />
                    <div>
                      <p className="text-sm text-[#78716C]">Confirmation sent to</p>
                      <p className="text-[#1C1917]">{booking.guest_email}</p>
                    </div>
                  </div>
                  <div className="border-t border-stone-100 pt-4 mt-4">
                    <div className="flex justify-between">
                      <span className="text-[#78716C]">Deposit Paid</span>
                      <span className="font-serif text-xl text-[#134E4A]">
                        $1,000
                      </span>
                    </div>
                    <p className="text-xs text-[#78716C] mt-2">
                      Total trip cost: ${booking.total_price?.toLocaleString()} (remaining payments due per schedule)
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <Button
                data-testid="back-to-home"
                onClick={() => navigate("/")}
                className="w-full bg-[#134E4A] hover:bg-[#0f3d3a] text-white py-6 text-sm uppercase tracking-widest"
              >
                Back to Home
              </Button>
              <p className="text-[#78716C] text-sm">
                A confirmation email will be sent to your inbox shortly.
              </p>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div data-testid="failed-state" className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="font-serif text-3xl text-[#1C1917] mb-4">
              Payment Failed
            </h1>
            <p className="text-[#78716C] mb-8">
              We couldn't process your payment. Please try again or contact us for assistance.
            </p>

            <div className="space-y-4">
              <Button
                onClick={() => navigate("/booking")}
                className="w-full bg-[#134E4A] hover:bg-[#0f3d3a] text-white py-6 text-sm uppercase tracking-widest"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="w-full py-6 text-sm uppercase tracking-widest"
              >
                Back to Home
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
